import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, ArrowRight, CheckCircle, Building2, MapPin, 
  User, Ruler, Recycle, Truck, FileText, CreditCard, AlertTriangle, Save, MessageSquare, Plus, X
} from "lucide-react";
import { toast } from "sonner";
import PortalWarningBanner from "../components/PortalWarningBanner";
import ContactSupportModal from "../components/ContactSupportModal";
import BrandWordmark from "../components/BrandWordmark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Project Info", icon: Building2 },
  { id: 2, title: "Type & Size", icon: Ruler },
  { id: 3, title: "Materials", icon: Recycle },
  { id: 4, title: "Facilities", icon: Truck },
  { id: 5, title: "Summary", icon: FileText },
  { id: 6, title: "Payment", icon: CreditCard }
];

const BUILDING_TYPES = [
  { value: "residential_new", label: "New Residential Construction" },
  { value: "residential_renovation", label: "Residential Renovation" },
  { value: "residential_addition", label: "Residential Addition" },
  { value: "residential_demolition", label: "Residential Demolition" },
  { value: "commercial_new", label: "New Commercial Construction" },
  { value: "commercial_renovation", label: "Commercial Renovation" },
  { value: "commercial_demolition", label: "Commercial Demolition" },
  { value: "industrial_new", label: "New Industrial Construction" },
  { value: "mixed_use", label: "Mixed-Use Development" }
];

const SCOPES = [
  { value: "new", label: "New Construction" },
  { value: "renovation", label: "Renovation" },
  { value: "addition", label: "Addition" },
  { value: "demolition", label: "Demolition" }
];

const ProjectWizard = ({ user, editMode = false }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [counties, setCounties] = useState([]);
  const [jurisdictionInfo, setJurisdictionInfo] = useState(null);
  const [wasteEstimate, setWasteEstimate] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedTier, setSelectedTier] = useState("cwmp");
  const [portalWarningAcknowledged, setPortalWarningAcknowledged] = useState(false);
  const [showPortalWarning, setShowPortalWarning] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState(editMode ? projectId : null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [bypassPayment, setBypassPayment] = useState(false);
  const [isFreeCwmpEligible, setIsFreeCwmpEligible] = useState(false);
  const [showCustomFacility, setShowCustomFacility] = useState(false);
  const [customFacility, setCustomFacility] = useState({
    name: "",
    address: "",
    diversion_rate: 65,
    materials: []
  });
  const [projectLoaded, setProjectLoaded] = useState(!editMode);
  const [showContactModal, setShowContactModal] = useState(false);
  const [pricing, setPricing] = useState({
    cwmp_price: 59,
    final_report_price: 49,
    bundle_price: 89,
    subscription_price: 99,
  });
  
  const [formData, setFormData] = useState({
    project_type: "cwmp",
    project_name: "",
    address: "",
    city: "",
    county: "",
    zip_code: "",
    permit_number: "",
    owner_name: user?.name || "",
    owner_email: user?.email || "",
    owner_phone: "",
    contractor_name: "",
    contractor_email: "",
    contractor_phone: "",
    contractor_license: "",
    building_type: "",
    scope: "",
    square_footage: "",
    valuation: "",
    stories: 1,
    construction_type: "wood",
    target_diversion: 65,
    compliance_path: "option1",
    subcontractor_acknowledgment: false,
    selectedFacilities: [],
    hauler_name: "",
    hauler_contact: ""
  });

  useEffect(() => {
    fetchCounties();
    checkAdminStatus();
    fetchPricing();
    // Load saved draft from localStorage
    const savedDraft = localStorage.getItem('projectWizardDraft');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        setFormData(prev => ({ ...prev, ...parsed.formData }));
        if (parsed.currentStep) setCurrentStep(parsed.currentStep);
        if (parsed.savedProjectId) setSavedProjectId(parsed.savedProjectId);
        toast.info("Draft loaded from previous session");
      } catch (e) {
        console.log("No valid draft found");
      }
    }
  }, []);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/check-admin`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        setBypassPayment(data.bypass_payment);
      }
    } catch (error) {
      console.log('Not admin');
    }
  };

  // Check free CWMP eligibility
  const checkFreeCwmpEligibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/user/free-cwmp-status`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setIsFreeCwmpEligible(data.eligible_for_free_cwmp);
      }
    } catch (error) {
      console.log('Could not check free CWMP eligibility');
    }
  };

  // Pull live pricing config so admin price changes propagate everywhere
  const fetchPricing = async () => {
    try {
      const r = await fetch(`${API}/pricing`);
      if (r.ok) {
        const data = await r.json();
        setPricing((prev) => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('Using default pricing');
    }
  };

  useEffect(() => {
    checkFreeCwmpEligibility();
  }, []);

  // Load existing project in edit mode
  useEffect(() => {
    const loadProject = async () => {
      if (!editMode || !projectId) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/projects/${projectId}`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          toast.error('Failed to load project');
          navigate('/dashboard');
          return;
        }
        
        const project = await response.json();
        
        // Map project data to form data
        setFormData({
          project_type: project.project_type || "cwmp",
          project_name: project.project_name || "",
          address: project.address || "",
          city: project.city || "",
          county: project.county || "",
          zip_code: project.zip_code || "",
          permit_number: project.permit_number || "",
          owner_name: project.owner_name || "",
          owner_email: project.owner_email || "",
          owner_phone: project.owner_phone || "",
          contractor_name: project.contractor_name || "",
          contractor_email: project.contractor_email || "",
          contractor_phone: project.contractor_phone || "",
          contractor_license: project.contractor_license || "",
          building_type: project.building_type || "",
          scope: project.scope || "",
          square_footage: project.square_footage?.toString() || "",
          valuation: project.valuation?.toString() || "",
          stories: project.stories?.toString() || "1",
          target_diversion: project.target_diversion || 65,
          compliance_path: project.compliance_path || "option1",
          subcontractor_acknowledgment: project.subcontractor_acknowledgment || false,
          selectedFacilities: project.facilities || [],
          hauler_name: project.haulers?.[0]?.name || "",
          hauler_contact: project.haulers?.[0]?.contact || ""
        });
        
        // Set waste estimate if available
        if (project.estimated_waste) {
          setWasteEstimate(project.estimated_waste);
        }
        
        // Set jurisdiction info
        if (project.jurisdiction_notes) {
          setJurisdictionInfo(project.jurisdiction_notes);
        }
        
        setProjectLoaded(true);
        toast.success('Project loaded for editing');
      } catch (error) {
        console.error('Error loading project:', error);
        toast.error('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, [editMode, projectId, navigate]);

  // Auto-save to localStorage when form changes (only for new projects)
  const autoSave = useCallback(() => {
    if (editMode) return; // Don't auto-save to localStorage in edit mode
    const draft = {
      formData,
      currentStep,
      savedProjectId,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('projectWizardDraft', JSON.stringify(draft));
  }, [formData, currentStep, savedProjectId, editMode]);

  useEffect(() => {
    // Debounced auto-save
    const timer = setTimeout(() => {
      if (formData.project_name) {
        autoSave();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData, autoSave]);

  useEffect(() => {
    if (formData.county) {
      fetchJurisdictionInfo(formData.county);
      fetchFacilities(formData.county);
      // Reset portal warning acknowledgment when county changes
      setPortalWarningAcknowledged(false);
    }
  }, [formData.county]);

  // Check if portal warning should be shown
  useEffect(() => {
    if (jurisdictionInfo?.portal_required?.mandatory && !portalWarningAcknowledged) {
      setShowPortalWarning(true);
    } else {
      setShowPortalWarning(false);
    }
  }, [jurisdictionInfo, portalWarningAcknowledged]);

  useEffect(() => {
    if (formData.building_type && formData.square_footage) {
      calculateWaste();
    }
  }, [formData.building_type, formData.square_footage]);

  const fetchCounties = async () => {
    try {
      const response = await fetch(`${API}/california/counties`);
      const data = await response.json();
      setCounties(data.counties || []);
    } catch (error) {
      console.error('Error fetching counties:', error);
    }
  };

  const fetchJurisdictionInfo = async (county) => {
    try {
      const response = await fetch(`${API}/california/jurisdiction/${county}`);
      const data = await response.json();
      setJurisdictionInfo(data.info);
    } catch (error) {
      console.error('Error fetching jurisdiction info:', error);
    }
  };

  const fetchFacilities = async (county) => {
    try {
      const response = await fetch(`${API}/california/facilities/${county}`);
      const data = await response.json();
      setFacilities(data.facilities || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const calculateWaste = async () => {
    try {
      const response = await fetch(
        `${API}/california/calculate-waste?building_type=${formData.building_type}&square_footage=${formData.square_footage}`
      , { method: 'POST' });
      const data = await response.json();
      setWasteEstimate(data);
    } catch (error) {
      console.error('Error calculating waste:', error);
    }
  };

  // Save draft to server
  const saveDraft = async () => {
    if (!formData.project_name || !formData.county) {
      toast.error("Please fill in project name and county first");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const projectData = {
        ...formData,
        square_footage: parseFloat(formData.square_footage) || 0,
        valuation: formData.valuation ? parseFloat(formData.valuation) : null,
        facilities: formData.selectedFacilities,
        haulers: formData.hauler_name ? [{ name: formData.hauler_name, contact: formData.hauler_contact }] : [],
        jurisdiction_notes: jurisdictionInfo
      };

      let response;
      if (savedProjectId) {
        // Update existing draft
        response = await fetch(`${API}/projects/${savedProjectId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
          body: JSON.stringify(projectData)
        });
      } else {
        // Create new draft
        response = await fetch(`${API}/projects`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
          body: JSON.stringify(projectData)
        });
      }

      if (response.ok) {
        const data = await response.json();
        setSavedProjectId(data.project_id);
        autoSave();
        toast.success("Draft saved!");
      } else {
        throw new Error('Failed to save draft');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const toggleFacility = (facility) => {
    const selected = formData.selectedFacilities;
    const exists = selected.find(f => f.name === facility.name);
    if (exists) {
      setFormData({
        ...formData,
        selectedFacilities: selected.filter(f => f.name !== facility.name)
      });
    } else {
      setFormData({
        ...formData,
        selectedFacilities: [...selected, facility]
      });
    }
  };

  const addCustomFacility = () => {
    if (!customFacility.name || !customFacility.address) {
      toast.error("Please enter facility name and address");
      return;
    }
    const newFacility = {
      ...customFacility,
      isCustom: true
    };
    setFormData({
      ...formData,
      selectedFacilities: [...formData.selectedFacilities, newFacility]
    });
    setCustomFacility({ name: "", address: "", diversion_rate: 65, materials: [] });
    setShowCustomFacility(false);
    toast.success("Custom facility added");
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.project_name || !formData.address || !formData.city || !formData.county) {
          toast.error("Please fill in all required fields");
          return false;
        }
        break;
      case 2:
        if (!formData.building_type || !formData.scope || !formData.square_footage) {
          toast.error("Please fill in all required fields");
          return false;
        }
        break;
      case 4:
        if (!formData.selectedFacilities || formData.selectedFacilities.length === 0) {
          toast.error("Please select at least one recycling facility");
          return false;
        }
        break;
    }
    return true;
  };

  const nextStep = async () => {
    if (validateStep()) {
      // Auto-save when moving to next step
      if (formData.project_name && formData.county) {
        await saveDraft();
      }
      setCurrentStep(Math.min(currentStep + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    // Data persists because it's in state - just change step
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  // Helper to clean form data before sending to API
  // Converts empty strings to null for optional fields (especially emails)
  const cleanProjectData = (data) => {
    const cleaned = { ...data };
    
    // Convert empty strings to null for optional email fields
    const emailFields = ['owner_email', 'contractor_email'];
    emailFields.forEach(field => {
      if (cleaned[field] === '' || cleaned[field] === undefined) {
        cleaned[field] = null;
      }
    });
    
    // Convert empty strings to null for other optional fields
    const optionalFields = ['zip_code', 'permit_number', 'owner_phone', 'contractor_name', 
                           'contractor_phone', 'contractor_license', 'construction_type'];
    optionalFields.forEach(field => {
      if (cleaned[field] === '' || cleaned[field] === undefined) {
        cleaned[field] = null;
      }
    });
    
    // Remove frontend-only fields
    delete cleaned.selectedFacilities;
    delete cleaned.hauler_name;
    delete cleaned.hauler_contact;
    
    return cleaned;
  };

  const handleSubmit = async () => {
    // Check if admin bypass
    if (bypassPayment) {
      // Skip payment, create project and go to dashboard
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const projectData = {
          ...cleanProjectData(formData),
          square_footage: parseFloat(formData.square_footage),
          valuation: formData.valuation ? parseFloat(formData.valuation) : null,
          facilities: formData.selectedFacilities || [],
          haulers: formData.hauler_name ? [{ name: formData.hauler_name, contact: formData.hauler_contact }] : [],
          jurisdiction_notes: jurisdictionInfo,
          product_type: selectedTier,
          payment_status: 'paid' // Admin bypass
        };

        let response;
        if (savedProjectId) {
          response = await fetch(`${API}/projects/${savedProjectId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            credentials: 'include',
            body: JSON.stringify({ ...projectData, payment_status: 'paid', tier: selectedTier })
          });
        } else {
          response = await fetch(`${API}/projects`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            credentials: 'include',
            body: JSON.stringify(projectData)
          });
        }

        if (response.ok) {
          localStorage.removeItem('projectWizardDraft');
          toast.success("Project created! (Admin bypass)");
          navigate('/dashboard');
        } else {
          throw new Error('Failed to create project');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Normal payment flow
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create project with cleaned data
      const projectData = {
        ...cleanProjectData(formData),
        square_footage: parseFloat(formData.square_footage),
        valuation: formData.valuation ? parseFloat(formData.valuation) : null,
        facilities: formData.selectedFacilities || [],
        haulers: formData.hauler_name ? [{ name: formData.hauler_name, contact: formData.hauler_contact }] : [],
        jurisdiction_notes: jurisdictionInfo
      };

      const response = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create project');
      }

      const project = await response.json();
      
      // Create checkout session
      const checkoutResponse = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.project_id,
          product_type: selectedTier,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Failed to create checkout');
      }

      const checkout = await checkoutResponse.json();
      
      // Check if it was free (first CWMP)
      if (checkout.status === 'free') {
        toast.success(checkout.message || 'Your first CWMP is free!');
        localStorage.removeItem('projectWizardDraft');
        navigate('/dashboard');
        return;
      }
      
      // Clear draft on successful checkout redirect
      localStorage.removeItem('projectWizardDraft');
      
      // Redirect to Stripe
      window.location.href = checkout.url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const bundleSavings = Math.max(0, (pricing.cwmp_price + pricing.final_report_price) - pricing.bundle_price);
  const PRICING = {
    cwmp: {
      price: pricing.cwmp_price,
      name: "CWMP Report",
      description: "Pre-construction waste plan",
      features: ["CALGreen compliant", "AI Compliance Memo (1 included)", "Instant PDF", "Jurisdiction notes"],
    },
    bundle: {
      price: pricing.bundle_price,
      name: "Bundle",
      description: "CWMP + Final Report",
      features: [
        "Both reports included",
        "Unlimited AI Compliance Memos",
        bundleSavings > 0 ? `Save $${bundleSavings}` : "Best value",
        "Complete compliance",
      ],
      recommended: true,
    },
    subscription: {
      price: pricing.subscription_price,
      name: "Unlimited",
      description: "Monthly subscription",
      features: ["Unlimited reports", "Unlimited AI Compliance Memos", "All features", "Cancel anytime"],
      interval: "month",
    },
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Before You Start - Quick Reference */}
            {!editMode && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Quick Tip: Have These Ready
                </h4>
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-blue-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span>Project address & permit number</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span>Building type & square footage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span>Owner contact details</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span>Contractor info (if applicable)</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-blue-700">Most users complete this in under 10 minutes</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="project_name" className="text-foreground text-lg font-medium">Project Name *</Label>
                <Input
                  id="project_name"
                  name="project_name"
                  placeholder="My Construction Project"
                  value={formData.project_name}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="permit_number" className="text-foreground text-lg font-medium">Permit Number</Label>
                <Input
                  id="permit_number"
                  name="permit_number"
                  placeholder="Optional"
                  value={formData.permit_number}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                  data-testid="permit-number-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-foreground text-lg font-medium">Project Address *</Label>
              <Input
                id="address"
                name="address"
                placeholder="123 Main Street"
                value={formData.address}
                onChange={handleInputChange}
                className="bg-input/50 border-primary/30"
                data-testid="address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-foreground text-lg font-medium">City *</Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="San Francisco"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground text-lg font-medium">County *</Label>
                <Select value={formData.county} onValueChange={(v) => handleSelectChange('county', v)}>
                  <SelectTrigger className="bg-input/50 border-primary/30" data-testid="county-select">
                    <SelectValue placeholder="Select county" />
                  </SelectTrigger>
                  <SelectContent>
                    {counties.map((county) => (
                      <SelectItem key={county} value={county}>{county}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code" className="text-foreground text-lg font-medium">ZIP Code</Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  placeholder="94102"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                  data-testid="zip-input"
                />
              </div>
            </div>

            {/* Portal Warning Banner for mandatory portal jurisdictions */}
            {showPortalWarning && jurisdictionInfo && (
              <PortalWarningBanner
                jurisdiction={jurisdictionInfo}
                onProceed={() => {
                  setPortalWarningAcknowledged(true);
                  setShowPortalWarning(false);
                  toast.info("Draft mode enabled. Your PDF will include portal submission instructions.");
                }}
                onDismiss={() => setShowPortalWarning(false)}
                showUpgrade={selectedTier !== 'premium'}
              />
            )}

            {/* Standard jurisdiction notice (non-portal) */}
            {jurisdictionInfo && jurisdictionInfo.notes && !jurisdictionInfo.portal_required?.mandatory && (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-500">Jurisdiction Notice</p>
                    <p className="text-sm text-muted-foreground mt-1">{jurisdictionInfo.notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Portal acknowledged notice */}
            {portalWarningAcknowledged && jurisdictionInfo?.portal_required?.mandatory && (
              <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-yellow-500">
                    Draft mode: PDF will include {jurisdictionInfo.portal_required.name} portal instructions
                  </span>
                </div>
                <button 
                  onClick={() => setShowPortalWarning(true)}
                  className="text-xs text-primary hover:underline"
                >
                  View details
                </button>
              </div>
            )}

            <div className="border-t border-primary/20 pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Owner Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="owner_name" className="text-foreground text-lg font-medium">Owner Name *</Label>
                  <Input
                    id="owner_name"
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                    data-testid="owner-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_email" className="text-foreground text-lg font-medium">Owner Email</Label>
                  <Input
                    id="owner_email"
                    name="owner_email"
                    type="email"
                    value={formData.owner_email}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_phone" className="text-foreground text-lg font-medium">Owner Phone</Label>
                  <Input
                    id="owner_phone"
                    name="owner_phone"
                    type="tel"
                    value={formData.owner_phone}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-primary/20 pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Contractor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contractor_name" className="text-foreground text-lg font-medium">Contractor Name</Label>
                  <Input
                    id="contractor_name"
                    name="contractor_name"
                    value={formData.contractor_name}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                    data-testid="contractor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contractor_license" className="text-foreground text-lg font-medium">License #</Label>
                  <Input
                    id="contractor_license"
                    name="contractor_license"
                    value={formData.contractor_license}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-foreground text-lg font-medium">Building Type *</Label>
                <Select value={formData.building_type} onValueChange={(v) => handleSelectChange('building_type', v)}>
                  <SelectTrigger className="bg-input/50 border-primary/30" data-testid="building-type-select">
                    <SelectValue placeholder="Select building type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUILDING_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground text-lg font-medium">Scope of Work *</Label>
                <Select value={formData.scope} onValueChange={(v) => handleSelectChange('scope', v)}>
                  <SelectTrigger className="bg-input/50 border-primary/30" data-testid="scope-select">
                    <SelectValue placeholder="Select scope" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPES.map((scope) => (
                      <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="square_footage" className="text-foreground text-lg font-medium">Square Footage *</Label>
                <Input
                  id="square_footage"
                  name="square_footage"
                  type="number"
                  placeholder="2,500"
                  value={formData.square_footage}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                  data-testid="sqft-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valuation" className="text-foreground text-lg font-medium">
                  Project Valuation ($) <span className="text-muted-foreground text-sm font-normal">(Optional)</span>
                </Label>
                <Input
                  id="valuation"
                  name="valuation"
                  type="number"
                  placeholder="e.g. 500000"
                  value={formData.valuation}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                />
                <p className="text-xs text-muted-foreground">Optional - Some jurisdictions require this for permit records</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stories" className="text-foreground text-lg font-medium">Stories</Label>
                <Input
                  id="stories"
                  name="stories"
                  type="number"
                  min="1"
                  value={formData.stories}
                  onChange={handleInputChange}
                  className="bg-input/50 border-primary/30"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground text-lg font-medium">Construction Type</Label>
              <Select value={formData.construction_type} onValueChange={(v) => handleSelectChange('construction_type', v)}>
                <SelectTrigger className="bg-input/50 border-primary/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wood">Wood Frame</SelectItem>
                  <SelectItem value="steel">Steel Frame</SelectItem>
                  <SelectItem value="concrete">Concrete</SelectItem>
                  <SelectItem value="masonry">Masonry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-primary/20 pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">CALGreen Compliance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-foreground text-lg font-medium">Compliance Path</Label>
                  <Select value={formData.compliance_path} onValueChange={(v) => handleSelectChange('compliance_path', v)}>
                    <SelectTrigger className="bg-input/50 border-primary/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="option1">Option 1 - 65% Diversion (Recommended)</SelectItem>
                      <SelectItem value="option2">Option 2 - 50% + Prescriptive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_diversion" className="text-foreground text-lg font-medium">Target Diversion %</Label>
                  <Input
                    id="target_diversion"
                    name="target_diversion"
                    type="number"
                    min="50"
                    max="100"
                    value={formData.target_diversion}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <h3 className="font-semibold text-foreground mb-2">Waste Estimate Calculated</h3>
              <p className="text-sm text-muted-foreground">
                Based on {formData.square_footage?.toLocaleString()} sq ft of {formData.building_type?.replace('_', ' ')}
              </p>
            </div>

            {wasteEstimate ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-primary/20">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{wasteEstimate.total_tons?.toFixed(1)}</p>
                      <p className="text-sm text-muted-foreground">Total Tons</p>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{wasteEstimate.total_lbs?.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Total Pounds</p>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{wasteEstimate.projected_diversion}%</p>
                      <p className="text-sm text-muted-foreground">Projected Diversion</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="materials-table">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 px-4 text-foreground">Material</th>
                        <th className="text-right py-3 px-4 text-foreground">Est. Tons</th>
                        <th className="text-right py-3 px-4 text-foreground">% of Total</th>
                        <th className="text-left py-3 px-4 text-foreground">Diversion Method</th>
                        <th className="text-right py-3 px-4 text-foreground">Diverted</th>
                        <th className="text-right py-3 px-4 text-foreground">Landfill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wasteEstimate.materials?.map((material, index) => (
                        <tr key={index} className="border-b border-primary/10">
                          <td className="py-3 px-4 text-foreground capitalize">
                            {material.material?.replace('_', ' ')}
                          </td>
                          <td className="py-3 px-4 text-right text-muted-foreground">
                            {material.estimated_tons?.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right text-muted-foreground">
                            {material.percentage?.toFixed(1)}%
                          </td>
                          <td className="py-3 px-4 text-muted-foreground capitalize">
                            {material.diversion_method?.replace('_', ' ')}
                          </td>
                          <td className="py-3 px-4 text-right text-green-400">
                            {material.diverted_tons?.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right text-red-400">
                            {material.landfill_tons?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Enter building type and square footage to calculate waste estimate</p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Suggested Facilities</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Based on your project location in {formData.county} County
              </p>
              
              <div className="grid gap-4">
                {facilities.map((facility, index) => (
                  <Card 
                    key={index}
                    className={`border cursor-pointer transition-all ${
                      formData.selectedFacilities.find(f => f.name === facility.name)
                        ? 'border-primary bg-primary/10'
                        : 'border-primary/20 hover:border-primary/40'
                    }`}
                    onClick={() => toggleFacility(facility)}
                    data-testid={`facility-card-${index}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-foreground">{facility.name}</h4>
                          <p className="text-sm text-muted-foreground">{facility.address}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {facility.materials?.map((mat, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {mat.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-500/20 text-green-400">
                            {facility.diversion_rate}% Diversion
                          </Badge>
                          {formData.selectedFacilities.find(f => f.name === facility.name) && (
                            <CheckCircle className="w-5 h-5 text-primary mt-2 ml-auto" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Custom Facility Option */}
              <div className="mt-6">
                {!showCustomFacility ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCustomFacility(true)}
                    className="w-full border-dashed border-primary/40 hover:border-primary/60 text-muted-foreground hover:text-foreground"
                    data-testid="add-custom-facility-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Facility
                  </Button>
                ) : (
                  <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-foreground">Add Custom Facility</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCustomFacility(false)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Facility Name *</Label>
                          <Input
                            placeholder="e.g., Local Recycling Center"
                            value={customFacility.name}
                            onChange={(e) => setCustomFacility({ ...customFacility, name: e.target.value })}
                            className="bg-input/50 border-primary/30"
                            data-testid="custom-facility-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Address *</Label>
                          <Input
                            placeholder="123 Main St, City, CA"
                            value={customFacility.address}
                            onChange={(e) => setCustomFacility({ ...customFacility, address: e.target.value })}
                            className="bg-input/50 border-primary/30"
                            data-testid="custom-facility-address"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Diversion Rate (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={customFacility.diversion_rate}
                          onChange={(e) => setCustomFacility({ ...customFacility, diversion_rate: parseInt(e.target.value) || 65 })}
                          className="bg-input/50 border-primary/30 w-32"
                          data-testid="custom-facility-diversion"
                        />
                        <p className="text-xs text-muted-foreground">Default: 65% (CALGreen minimum)</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Materials Accepted</Label>
                        <div className="flex flex-wrap gap-2">
                          {['Concrete', 'Metal', 'Wood', 'Drywall', 'Cardboard', 'Asphalt', 'Mixed C&D'].map((mat) => (
                            <Badge
                              key={mat}
                              variant={customFacility.materials.includes(mat) ? "default" : "outline"}
                              className={`cursor-pointer transition-all ${
                                customFacility.materials.includes(mat) 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'hover:bg-primary/20'
                              }`}
                              onClick={() => {
                                const materials = customFacility.materials.includes(mat)
                                  ? customFacility.materials.filter(m => m !== mat)
                                  : [...customFacility.materials, mat];
                                setCustomFacility({ ...customFacility, materials });
                              }}
                              data-testid={`material-${mat.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {mat}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">Click to select materials this facility accepts</p>
                      </div>
                      <Button
                        type="button"
                        onClick={addCustomFacility}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="save-custom-facility-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Add Facility
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            <div className="border-t border-primary/20 pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Hauler Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="hauler_name" className="text-foreground text-lg font-medium">Hauler Name</Label>
                  <Input
                    id="hauler_name"
                    name="hauler_name"
                    placeholder="ABC Hauling Services"
                    value={formData.hauler_name}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                    data-testid="hauler-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hauler_contact" className="text-foreground text-lg font-medium">Hauler Contact</Label>
                  <Input
                    id="hauler_contact"
                    name="hauler_contact"
                    placeholder="(555) 123-4567"
                    value={formData.hauler_contact}
                    onChange={handleInputChange}
                    className="bg-input/50 border-primary/30"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-4">
              <Checkbox
                id="subcontractor_acknowledgment"
                checked={formData.subcontractor_acknowledgment}
                onCheckedChange={(checked) => setFormData({ ...formData, subcontractor_acknowledgment: checked })}
                data-testid="subcontractor-checkbox"
              />
              <Label htmlFor="subcontractor_acknowledgment" className="text-sm text-muted-foreground">
                All subcontractors have been notified of waste diversion requirements
              </Label>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground">Project Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Project Name</p>
                    <p className="text-foreground font-medium">{formData.project_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Location</p>
                    <p className="text-foreground font-medium">{formData.city}, {formData.county}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Building Type</p>
                    <p className="text-foreground font-medium capitalize">{formData.building_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Square Footage</p>
                    <p className="text-foreground font-medium">{parseFloat(formData.square_footage).toLocaleString()} sq ft</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Estimated Waste</p>
                    <p className="text-foreground font-medium">{wasteEstimate?.total_tons?.toFixed(1)} tons</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Target Diversion</p>
                    <p className="text-foreground font-medium">{formData.target_diversion}%</p>
                  </div>
                </div>

                {jurisdictionInfo?.notes && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 mt-4">
                    <p className="text-sm text-yellow-500">
                      <strong>Note:</strong> {jurisdictionInfo.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground">Selected Facilities ({formData.selectedFacilities.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {formData.selectedFacilities.length > 0 ? (
                  <ul className="space-y-2">
                    {formData.selectedFacilities.map((facility, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-foreground">{facility.name}</span>
                        <Badge variant="outline" className="text-xs ml-auto">{facility.diversion_rate}%</Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No facilities selected</p>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            {/* Admin bypass notice */}
            {bypassPayment && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-green-500">Admin Access Enabled</p>
                    <p className="text-sm text-muted-foreground">Payment bypass active - you can generate plans without payment.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">Choose Your Plan</h3>
              <p className="text-muted-foreground">Simple, transparent pricing for your project</p>
              {isFreeCwmpEligible && (
                <div className="mt-3 inline-block bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold">
                  🎉 Your first CWMP is FREE!
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {Object.entries(PRICING).map(([key, tier]) => (
                <Card
                  key={key}
                  className={`cursor-pointer transition-all relative ${
                    selectedTier === key
                      ? 'border-primary shadow-lg glow'
                      : 'border-primary/20 hover:border-primary/40'
                  }`}
                  onClick={() => setSelectedTier(key)}
                  data-testid={`tier-${key}`}
                >
                  {tier.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                      Best Value
                    </div>
                  )}
                  {key === 'cwmp' && isFreeCwmpEligible && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      FREE
                    </div>
                  )}
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="text-foreground">{tier.name}</CardTitle>
                    <div className="mt-2">
                      {key === 'cwmp' && isFreeCwmpEligible ? (
                        <>
                          <span className="text-3xl font-bold text-green-400">$0</span>
                          <span className="text-sm text-muted-foreground line-through ml-2">${tier.price}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-primary">${tier.price}</span>
                          {tier.interval && <span className="text-sm text-muted-foreground">/mo</span>}
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{tier.description}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {tier.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-center">
                      {selectedTier === key ? (
                        <CheckCircle className="w-8 h-8 text-primary" />
                      ) : (
                        <div className="w-8 h-8 rounded-full border-2 border-primary/30" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-primary/20 bg-primary/5 max-w-4xl mx-auto">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-foreground">Total Amount</p>
                    <p className="text-sm text-muted-foreground">
                      {PRICING[selectedTier].name} for {formData.project_name || 'your project'}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-primary">
                    {selectedTier === 'cwmp' && isFreeCwmpEligible ? (
                      <span className="text-green-400">FREE</span>
                    ) : (
                      `$${PRICING[selectedTier].price}`
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state while loading project in edit mode
  if (editMode && loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
              <BrandWordmark size="md" />
            </div>
            {editMode && (
              <Badge variant="outline" className="border-yellow-500/50 text-yellow-400">
                Editing Project
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex flex-col items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center mb-2
                  ${currentStep === step.id ? 'bg-primary text-primary-foreground' : 
                    currentStep > step.id ? 'bg-primary/30 text-primary border border-primary' : 
                    'bg-muted text-muted-foreground border border-primary/30'}
                `}>
                  {currentStep > step.id ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <span className={`text-base font-semibold hidden md:block ${
                  currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </span>
                {index < STEPS.length - 1 && (
                  <div className={`hidden md:block h-0.5 flex-1 mx-2 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-primary/20'
                  }`} style={{ position: 'absolute', left: '50%', right: '-50%', top: '24px' }} />
                )}
              </div>
            ))}
          </div>
          <Progress value={(currentStep / STEPS.length) * 100} className="h-2" />
        </div>

        {/* Step Content */}
        <Card className="border-primary/20 mb-8" data-testid="wizard-content">
          <CardHeader>
            <CardTitle className="text-2xl text-foreground flex items-center gap-3">
              {(() => { const Icon = STEPS[currentStep - 1].icon; return <Icon className="w-7 h-7 text-primary" />; })()}
              {STEPS[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-primary/30"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {/* Save Draft Button */}
          <Button
            variant="ghost"
            onClick={saveDraft}
            disabled={saving || !formData.project_name}
            className="text-muted-foreground hover:text-foreground"
            data-testid="save-draft-btn"
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </>
            )}
          </Button>
          
          {currentStep < STEPS.length ? (
            <Button
              onClick={nextStep}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : bypassPayment ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Project (Admin)
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          )}
        </div>
      </main>

      {/* Floating Contact Support Button */}
      <div className="fixed bottom-20 right-6 z-40">
        <Button
          onClick={() => setShowContactModal(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full px-4 py-6"
          data-testid="contact-support-btn"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Support
        </Button>
      </div>

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
    </div>
  );
};

export default ProjectWizard;
